import { useEffect, useState } from "react";
import axios from "axios";
import useEmblaCarousel from "embla-carousel-react";
import { Reveal } from "@/lib/motion";
import { ArrowLeft, ArrowRight, MapPin } from "lucide-react";
import { useSiteContent } from "@/lib/useSiteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrendingProjects({ onRequestDetails }) {
  const c = useSiteContent().trending;
  const [items, setItems] = useState([]);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
    containScroll: "trimSnaps",
  });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/trending-projects`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
    };
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    emblaApi.reInit();
    onSelect();
  }, [emblaApi, items.length]);

  return (
    <section
      id="trending"
      data-testid="trending-section"
      className="relative py-24 md:py-32 bg-[#0A0A0B] text-white overflow-hidden"
    >
      <div className="absolute -top-24 left-1/3 w-[800px] h-[800px] rounded-full bg-[#B68D40]/8 blur-3xl pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8">
          <div className="max-w-3xl">
            {c.eyebrow && (
              <Reveal>
                <p className="eyebrow text-[#C9A469]">{c.eyebrow}</p>
              </Reveal>
            )}
            <Reveal delay={0.05}>
              <h2 className="font-serif-luxe text-4xl md:text-6xl leading-[1.02] mt-2 text-white">
                {c.heading}
              </h2>
            </Reveal>
            {c.intro && (
              <Reveal delay={0.1}>
                <p className="text-white/70 mt-8 leading-relaxed max-w-2xl">{c.intro}</p>
              </Reveal>
            )}
            {c.sub_intro && (
              <Reveal delay={0.15}>
                <p className="text-white/70 mt-4 leading-relaxed max-w-2xl">{c.sub_intro}</p>
              </Reveal>
            )}
          </div>

          <Reveal delay={0.15}>
            <div className="hidden md:flex gap-3 mt-2">
              <button
                data-testid="trending-prev"
                aria-label="Previous"
                disabled={!canPrev}
                onClick={() => emblaApi?.scrollPrev()}
                className={`w-14 h-14 rounded-full border flex items-center justify-center transition-all ${
                  canPrev
                    ? "border-white/40 text-white hover:bg-white hover:text-[#0A0A0B]"
                    : "border-white/15 text-white/25 cursor-not-allowed"
                }`}
              >
                <ArrowLeft size={18} strokeWidth={1.3} />
              </button>
              <button
                data-testid="trending-next"
                aria-label="Next"
                disabled={!canNext}
                onClick={() => emblaApi?.scrollNext()}
                className={`w-14 h-14 rounded-full border flex items-center justify-center transition-all ${
                  canNext
                    ? "border-white/40 text-white hover:bg-white hover:text-[#0A0A0B]"
                    : "border-white/15 text-white/25 cursor-not-allowed"
                }`}
              >
                <ArrowRight size={18} strokeWidth={1.3} />
              </button>
            </div>
          </Reveal>
        </div>

        {/* Cards */}
        <div
          className="mt-14 overflow-hidden -mx-6 md:-mx-10"
          ref={emblaRef}
          data-testid="trending-carousel"
        >
          <div className="flex">
            {items.map((p, i) => (
              <div
                key={p.id}
                className="min-w-0 shrink-0 grow-0 basis-[85%] sm:basis-[60%] md:basis-[40%] lg:basis-[24%] px-4 first:pl-6 md:first:pl-10 last:pr-6 md:last:pr-10"
                data-testid={`trending-card-${i}`}
              >
                <div className="bg-neutral-900/40 border border-white/5 rounded-2xl overflow-hidden h-full flex flex-col">
                  {/* Image with COMING SOON overlay */}
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={p.image_url}
                      alt={p.name}
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ filter: "blur(2px) brightness(0.55)" }}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div
                        data-testid={`trending-card-comingsoon-${i}`}
                        className="font-serif-luxe text-white text-2xl md:text-3xl tracking-[0.16em] drop-shadow-[0_4px_20px_rgba(0,0,0,0.6)]"
                      >
                        {c.coming_soon_label || "COMING SOON"}
                      </div>
                    </div>
                    {p.is_pre_launch && (
                      <span
                        data-testid={`trending-card-badge-${i}`}
                        className="absolute top-4 left-4 bg-[#D2323A] text-white text-[0.72rem] font-semibold tracking-wide px-3 py-1 rounded"
                      >
                        {c.pre_launch_label || "Pre-Launch"}
                      </span>
                    )}
                  </div>

                  {/* Card body */}
                  <div className="p-6 md:p-7 flex-1 flex flex-col">
                    <div
                      className="font-serif-luxe text-xl md:text-2xl text-white leading-none"
                      data-testid={`trending-card-price-${i}`}
                    >
                      Price On Request
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-white/85">
                      <MapPin size={15} strokeWidth={1.4} className="text-[#C9A469] shrink-0" />
                      <span className="text-sm" data-testid={`trending-card-location-${i}`}>
                        {p.location || p.name}
                      </span>
                    </div>

                    <p className="mt-4 text-white/60 text-sm leading-relaxed flex-1">
                      {p.description || "New masterplan offering Townhouses, Villas and Apartments."}
                    </p>

                    <button
                      type="button"
                      onClick={() => onRequestDetails?.(p)}
                      data-testid={`trending-card-cta-${i}`}
                      className="mt-6 w-full inline-flex items-center justify-center gap-2 border border-white/50 hover:border-white text-white text-sm tracking-[0.14em] uppercase py-3.5 rounded-full transition-all hover:bg-white hover:text-[#0A0A0B]"
                    >
                      {c.cta_label || "Enquire Now"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {items.length === 0 && (
          <p className="text-center text-white/50 mt-16" data-testid="trending-empty">
            No upcoming projects listed yet.
          </p>
        )}

        <div className="md:hidden mt-8 flex justify-center gap-3">
          <button
            aria-label="Previous"
            disabled={!canPrev}
            onClick={() => emblaApi?.scrollPrev()}
            className={`w-11 h-11 rounded-full border flex items-center justify-center transition-all ${
              canPrev ? "border-white/40 text-white" : "border-white/10 text-white/25"
            }`}
          >
            <ArrowLeft size={16} strokeWidth={1.4} />
          </button>
          <button
            aria-label="Next"
            disabled={!canNext}
            onClick={() => emblaApi?.scrollNext()}
            className={`w-11 h-11 rounded-full border flex items-center justify-center transition-all ${
              canNext ? "border-white/40 text-white" : "border-white/10 text-white/25"
            }`}
          >
            <ArrowRight size={16} strokeWidth={1.4} />
          </button>
        </div>
      </div>
    </section>
  );
}